<template>
  <v-card title="Branch Admins">
    <template v-slot:append>
      <v-icon icon="mdi-account-plus" @click="openModal('addBranchAdmin')"></v-icon>
      <v-tooltip activator="parent" location="end">Add Branch Admin</v-tooltip>
    </template>
    <v-card-text>
      <v-data-table-server
        v-model="branchAdmins.selected"
        :headers="branchAdmins.headers"
        :items="branchAdmins.data"
        :items-per-page="branchAdmins.pageOptions.limit"
        item-key="id"
        :items-length="branchAdmins.count"
        :search="branchAdmins.pageOptions.q"
        @update:options="handleTeamMemberChange">
        <template v-slot:[`item.action`]="{ item }">
          <v-row justify="end">
            <v-col cols="auto">
              <div @click="openModal('removeBranchAdmin', item)">
                <v-icon color="error">mdi-delete </v-icon>
                <v-tooltip activator="parent" location="end">Remove User </v-tooltip>
              </div>
            </v-col></v-row
          >
        </template>
        <template v-slot:top>
          <v-text-field
            v-model="branchAdmins.pageOptions.q"
            class="ma-2"
            density="compact"
            placeholder="Search branch admins..."
            hide-details></v-text-field>
        </template>
      </v-data-table-server>
    </v-card-text>
  </v-card>

  <v-dialog v-model="addBranchAdmin.isAddOpen" max-width="700px">
    <v-card>
      <v-card-title>Add Branch Admin</v-card-title>
      <v-card-text>
        <v-data-table-server
          v-model="addBranchAdmin.selected"
          :headers="addBranchAdmin.headers"
          :items="addBranchAdmin.data"
          :items-per-page="addBranchAdmin.pageOptions.limit"
          item-key="id"
          show-select
          :items-length="addBranchAdmin.count"
          :search="addBranchAdmin.pageOptions.q"
          @update:options="handleAddBranchAdminChange">
          <template v-slot:top>
            <v-text-field
              v-model="addBranchAdmin.pageOptions.q"
              class="ma-2"
              density="compact"
              placeholder="Search users members..."
              hide-details></v-text-field>
          </template>
        </v-data-table-server>
      </v-card-text>
      <v-card-actions>
        <v-btn color="secondary" @click="addBranchAdmin.isAddOpen = false">Cancel</v-btn>
        <v-btn color="primary" @click="addBranchAdmins">Add</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <ConfirmDeleteComponent
    :description="`${addBranchAdmin.currentBranchAdmin.first_name} ${addBranchAdmin.currentBranchAdmin.last_name}`"
    title="Branch Admin"
    item="Branch Admin"
    :isOpen="addBranchAdmin.isRemoveOpen"
    @actionCanceled="addBranchAdmin.isRemoveOpen = false"
    @actionConfirmed="deleteBranchAdmin"></ConfirmDeleteComponent>
</template>
<script setup lang="ts">
import type { VDataTableServer } from 'vuetify/components';
import { BranchDTO, IBranchDTO, IPaginationDTO, IUserDTO, PaginationDTO, UserDTO } from '@/dtos';
import { computed, ref, watch } from 'vue';
import ConfirmDeleteComponent from '@/components/ui/ConfirmDeleteComponent.vue';
import { useBranchYearStore } from '@/stores/modules/admin';
import { useUpdateStatusStore } from '@/stores/modules';

interface Props {
  branch: IBranchDTO;
}
const props = defineProps<Props>();
const emit = defineEmits(['snack']);
const branch = ref<IBranchDTO>(new BranchDTO());
const branchYearStore = useBranchYearStore();
const statusStore = useUpdateStatusStore();
watch(
  () => props.branch,
  (newVal) => {
    if (newVal) {
      branch.value = new BranchDTO(newVal);
    }
  },
  { immediate: true },
);
const branchId = computed(() => branch.value.id);
const pageOptionsBranchAdmins = ref<IPaginationDTO>(new PaginationDTO());
const { data: branchAdminData, isLoading: isLoadingeBranchAdmins } =
  branchYearStore.getBranchAdminsQuery(branchId, pageOptionsBranchAdmins);

const branchAdmins = ref({
  pageOptions: pageOptionsBranchAdmins,
  count: computed(() => branchAdminData.value?.count ?? 0),
  data: computed(() => branchAdminData.value?.data),
  loading: computed(() => isLoadingeBranchAdmins),
  selected: [] as string[],
  headers: [
    { title: 'First Name', key: 'first_name', align: 'start', sortable: true },
    { title: 'Last Name', key: 'last_name', align: 'start', sortable: true },
    { title: 'Email', key: 'email_address', align: undefined, sortable: true },
    { title: '', key: 'action', align: 'end', sortable: false },
  ] as const,
});

const pageOptionsPossibleBranchAdmins = ref<IPaginationDTO>(new PaginationDTO());
const { data: possibleBranchAdminData, isLoading: isLoadingPossibleBranchAdmins } =
  branchYearStore.getPossibleBranchAdminsQuery(branchId, pageOptionsPossibleBranchAdmins);

const addBranchAdmin = ref({
  pageOptions: pageOptionsPossibleBranchAdmins,
  count: computed(() => possibleBranchAdminData.value?.count ?? 0),
  currentBranchAdmin: new UserDTO() as IUserDTO,
  isAddOpen: false,
  isRemoveOpen: false,
  data: computed(() => possibleBranchAdminData.value?.data),
  loading: computed(() => isLoadingPossibleBranchAdmins),
  selected: [] as string[],
  headers: [
    { title: 'First Name', key: 'first_name', align: 'start', sortable: true },
    { title: 'Last Name', key: 'last_name', align: 'start', sortable: true },
    { title: 'Email', key: 'email_address', align: undefined, sortable: true },
    { title: '', key: 'action', align: 'end', sortable: false },
  ] as const,
});

const handleTeamMemberChange = (value: VDataTableServer) => {
  pageOptionsBranchAdmins.value.buildPagination(value);
};
const handleAddBranchAdminChange = (value: VDataTableServer) => {
  pageOptionsPossibleBranchAdmins.value.buildPagination(value);
};

const addBranchAdmins = async () => {
  branchYearStore.addAdminToBranchYear.mutateAsync({
    branchId: branch.value.id,
    ids: addBranchAdmin.value.selected,
  });
  addBranchAdmin.value.selected = [];
  addBranchAdmin.value.isAddOpen = false;
};

const deleteBranchAdmin = async () => {
  branchYearStore.removeBranchYearAdmin.mutate({
    branchId: branch.value.id,
    userId: addBranchAdmin.value.currentBranchAdmin.id,
  });
  addBranchAdmin.value.isRemoveOpen = false;
};

const updateStatus = computed(() => statusStore.getStatus(keyUpdate.value));
const keyUpdate = computed(() => `branch-year-admin-${addBranchAdmin.value.currentBranchAdmin.id}`);
watch(updateStatus, (value) => {
  if (!value) return;

  emit('snack', 'Branch Year Admin', value, statusStore.getError(keyUpdate.value));
});
const addStatus = computed(() => statusStore.getStatus('branch-year-admin-add'));
watch(addStatus, (value) => {
  if (!value) return;

  emit('snack', 'Branch Year Admin', value, statusStore.getError('branch-year-admin-add'));
});

const openModal = (type: string, item = {}) => {
  if (type === 'addBranchAdmin') {
    addBranchAdmin.value.pageOptions.q = '';
    addBranchAdmin.value.selected = [];
    addBranchAdmin.value.isAddOpen = true;
  } else if (type === 'removeBranchAdmin') {
    addBranchAdmin.value.currentBranchAdmin = item as IUserDTO;
    addBranchAdmin.value.isRemoveOpen = true;
  }
};
</script>
